<template>
  <div class="field-list-container my-5 p-4 border rounded shadow-sm bg-light">
    <label>
      <question-display :question="question" customClass="header-fieldlist"></question-display>
    </label>

    <div v-for="(fieldSet, index) in fieldList" :key="fieldSet.id || index" class="field-set mb-4 row">
      <label style="font-size: small; padding-left: 16px; color: gray;">Destination #{{ index + 1 }}</label>

      <template v-for="field in (question.fields || [])" :key="field.key_name">
        <div class="form-group mb-3" v-if="field && (!field.condition || checkFieldCondition(field))">
        <label v-if="field.display_label">{{ field.field_name }}</label>

        <!-- Textbox -->
        <div class="col-md-12 labelSpacing" v-if="field.type === 'textbox'">
          <input
            type="text"
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]"
            class="form-control mb-3"
            @input="emitFieldListUpdate"
          />
        </div>

        <!-- Textarea -->
        <div class="col-md-12 labelSpacing" v-if="field.type === 'textarea'">
          <textarea 
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]"
            class="form-control mb-3"
            @input="emitFieldListUpdate"
          ></textarea>
        </div>

        <!-- Dropdown -->
        <div class="col-md-6 labelSpacing" v-if="field.type === 'dropdown'">
          <select 
            :placeholder="field.placeholder"
            v-model="fieldSet[field.key_name]" 
            class="form-control form-select"
          >
            <option value="" disabled>Select an option</option>
            <option 
              v-for="option in dropdownOptions[field.key_name]" 
              :key="option.value" 
              :value="option.value"
            >
              {{ option.name }}
            </option>
          </select>
        </div>

        <!-- Multiselect Dropdown for Country -->
        <div class="col-md-6 labelSpacing" v-if="field.type === 'dropdown-search'">
          <multiselect
          v-model="fieldSet[field.key_name]"
          :options="dropdownOptions[field.key_name] || []"
          :searchable="true"
          :close-on-select="true"
          :clear-on-select="false"
          label="name"
          track-by="value"
          :placeholder="field.placeholder || 'Select Country'"
          @update:modelValue="emitFieldListUpdate"
        />

        </div>

        <!-- Number input -->
        <div class="col-md-6 labelSpacing" v-if="field.type === 'number'">
          <input
            type="number"
            autocomplete="off"
            v-model="fieldSet[field.key_name]"
            class="form-control"
            :placeholder="field.placeholder"
            @input="updateInput($event, field, fieldSet)" 
          />
        </div>

        <!-- Radio input -->
        <div v-if="field.type === 'radio'" class="form-check col-sm-12 labelSpacing">
          <label class="mr-2" style="margin-right: 1rem;">{{ field.header }}</label>
          <label class="form-label" v-for="option in field.options" :key="option">
            <input 
              type="radio" 
              :value="option" 
              v-model="fieldSet[field.key_name]" 
              class="form-check-input"
            />
            {{ option }}&nbsp;&nbsp;&nbsp;&nbsp;
          </label>
        </div>
        </div>
      </template>
    </div>

    <!-- Action Buttons -->
    <div class="form-row justify-content-center mt-4">
      <div class="col-12 col-md-5 mb-3">
        <button 
          type="button"
          class="btn btn-danger btn-block btn-add"
          :disabled="fieldList.length <= 1"
          @click="removeFieldSet(fieldList.length - 1)"
        >
          <i class="fa fa-trash"></i> Remove City/Attraction
        </button>
      </div>

      <div class="col-12 col-md-5 mb-3">
        <button 
          type="button"
          class="btn btn-primary btn-block btn-add"
          :disabled="fieldList.length >= 7"
          @click="addFieldSet"
        >
          <i class="fa fa-plus"></i> Add City/Attraction
        </button>
      </div>

      <div class="col-12 text-center">
        <small v-if="fieldList.length >= 7" class="text-danger">
          Max number of destinations reached
        </small>
      </div>
    </div>

    <div class="col-sm-12 text-center mt-2">
      <small v-if="showValidation && required && !fieldList.length" class="text-danger">
        This field is required.
      </small>
    </div>
  </div>
</template>

<script>
import dropdownValues from "@/assets/data/dropdown-values/dropdown.json";
import surveyService from '@/services/surveyService';
import QuestionDisplay from '../utils/QuestionDisplay.vue';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.css';

export default {
  components: {
    QuestionDisplay,
    Multiselect
  },
  props: {
    question: Object,
    required: Boolean,
    showValidation: Boolean
  },
  data() {
    return {
      fieldList: (this.question?.id ? surveyService.getAnswer(this.question.id) : null) || [],
      dropdownOptions: {}
    };
  },
  methods: {
    checkFieldCondition(field) {
      if (!field.condition) return true;
      
      const conditionAnswer = surveyService.getAnswer(field.condition.question_id);
      
      // Handle object answers (like dropdown selections)
      let answerValue = conditionAnswer;
      if (conditionAnswer && typeof conditionAnswer === 'object') {
        answerValue = conditionAnswer.key || conditionAnswer.value || conditionAnswer;
      }
      
      const isMatch = answerValue?.toString() === field.condition.value?.toString();
      return field.condition.reverse ? !isMatch : isMatch;
    },

    updateInput(event, field, fieldSet) {
      const value = Number(event.target.value);
      fieldSet[field.key_name] = value >= 0 && !isNaN(value) ? value : '';
      this.emitFieldListUpdate();
    },

    emitFieldListUpdate() {
      // Clean up hidden fields from all destinations before saving
      this.fieldList.forEach(fieldSet => {
        this.question.fields?.forEach(field => {
          if (field?.key_name && field.condition && !this.checkFieldCondition(field)) {
            // Field is hidden by condition - remove it from the fieldSet
            delete fieldSet[field.key_name];
            console.log(`🧹 Cleaned hidden field "${field.key_name}" from destination`);
          }
        });
        
        // CRITICAL: Clean up Q11 destinations — enforce mutual exclusion of state vs country
        if (this.question?.id === '11') {
          // Extract raw values from multiselect objects if needed
          const rawState = fieldSet.state?.value ?? fieldSet.state;
          const rawCountry = fieldSet.country?.value ?? fieldSet.country;

          // Remove invalid codes
          if (rawState === '501' || rawState === '999') {
            delete fieldSet.state;
          }
          if (rawCountry === '501' || rawCountry === '999') {
            delete fieldSet.country;
          }

          // Re-read after cleanup
          const stateVal = fieldSet.state?.value ?? fieldSet.state;
          const countryVal = fieldSet.country?.value ?? fieldSet.country;

          if (stateVal && countryVal) {
            const stateNum = parseInt(stateVal);
            const countryNum = parseInt(countryVal);

            if (!isNaN(stateNum) && stateNum >= 1 && stateNum <= 78) {
              // Valid US state selected — clear country
              console.log(`🧹 Q11: US state "${stateVal}" selected, clearing country`);
              delete fieldSet.country;
            } else if (!isNaN(countryNum) && countryNum >= 10000) {
              // Valid international country selected — clear state
              console.log(`🧹 Q11: International country "${countryVal}" selected, clearing state`);
              delete fieldSet.state;
            } else {
              // Both invalid — clear both
              delete fieldSet.state;
              delete fieldSet.country;
            }
          }
        }
      });
      
      surveyService.setAnswer(this.question.id, this.fieldList);
      this.$emit("input", this.fieldList);
    },

    addFieldSet() {
      if (this.fieldList.length >= 7) return;
      if (!this.question?.fields) return;

      const newFieldSet = { id: crypto.randomUUID?.() || Date.now() };
      this.question.fields.forEach(field => {
        if (field?.key_name) {
          // Initialize field based on whether it should be shown
          if (field.condition && !this.checkFieldCondition(field)) {
            // Field is hidden by condition - don't add it to the fieldSet
            // This prevents hidden fields from retaining old values
            return;
          }
          newFieldSet[field.key_name] = "";
        }
      });
      this.fieldList.push(newFieldSet);
      this.emitFieldListUpdate();
    },

    removeFieldSet(index) {
      this.fieldList.splice(index, 1);
      this.emitFieldListUpdate();
    },

    loadDropdownValues() {
      this.dropdownOptions = {};

      this.question.fields?.forEach(field => {
        if ((field.type === 'dropdown' || field.type === 'dropdown-search') && field['selection-list']) {
          const key = field['selection-list'].value;
          const options = dropdownValues[key];

         this.dropdownOptions[field.key_name] = Array.isArray(options) ? options : [];
        }
      });
    },


    initializeFieldList() {
      if (!this.fieldList || !this.fieldList.length) {
        this.addFieldSet();
      }
    },

    cleanupHiddenFields() {
      // Clean up fields that are now hidden due to condition changes
      let hasChanges = false;
      this.fieldList.forEach(fieldSet => {
        this.question.fields?.forEach(field => {
          if (field?.key_name && field.condition && !this.checkFieldCondition(field)) {
            if (fieldSet[field.key_name] !== undefined) {
              delete fieldSet[field.key_name];
              hasChanges = true;
              console.log(`🧹 Cleaned hidden field "${field.key_name}" due to condition change`);
            }
          }
        });
      });
      
      if (hasChanges) {
        this.emitFieldListUpdate();
      }
    }
  },
  watch: {
    question: {
      handler(newQuestion, oldQuestion) {
        // Only reload if the question ID actually changed
        if (newQuestion?.id && newQuestion?.id !== oldQuestion?.id) {
          this.fieldList = surveyService.getAnswer(newQuestion.id) || [];
          this.dropdownOptions = {};
          this.loadDropdownValues();
          this.initializeFieldList();
        }
      },
      deep: true,
      immediate: false
    }
  },
  created() {
    // Watch for changes to Q3A (residency status) to clean up Q11 destinations
    this.$watch(
      () => surveyService.getAnswer('3a'),
      (newVal, oldVal) => {
        if (newVal !== oldVal && this.question?.id === '11') {
          console.log('🔄 Q3A changed, cleaning up Q11 hidden fields');
          this.cleanupHiddenFields();
        }
      },
      { deep: true }
    );
  },
  mounted() {
    this.loadDropdownValues();
    this.initializeFieldList();
  }
};
</script>

<style scoped>
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type=number] {
  -moz-appearance: textfield;
}

.btn-add {
  margin-bottom: 1rem;
}

.labelSpacing {
  margin-bottom: 1rem;
}
</style>
